import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { ExchangeRates } from '@flows.cash/types'
import { exchangeRateService } from '../services/exchangeRates'

// Track ongoing operations to prevent duplicates
let isUpdateInProgress = false;
let updateTimeout: ReturnType<typeof setTimeout> | null = null;
const UPDATE_DEBOUNCE_MS = 2000; // 2 seconds debounce

interface CurrencyStore {
  rates: ExchangeRates | null
  lastUpdate: number | null
  isLoading: boolean
  error: string | null
  conversionCache: Record<string, number>
  fetchRates: (currencies?: string[]) => Promise<ExchangeRates | null>
  updateRates: (currencies?: string[]) => Promise<void>
  convertAmount: (amount: number, fromCurrency: string, toCurrency: string) => number
  clearRates: () => void
}

/**
 * Log levels for standardized logging
 */
enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

/**
 * Standardized logger for currency operations
 */
const logger = {
  debug: (message: string, ...args: any[]) => {
    console.debug(`[CurrencyStore][${LogLevel.DEBUG}] ${message}`, ...args)
  },
  info: (message: string, ...args: any[]) => {
    console.log(`[CurrencyStore][${LogLevel.INFO}] ${message}`, ...args)
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(`[CurrencyStore][${LogLevel.WARN}] ${message}`, ...args)
  },
  error: (message: string, error?: any) => {
    console.error(`[CurrencyStore][${LogLevel.ERROR}] ${message}`, error instanceof Error ? error.message : error)
  }
}

export const useCurrencyStore = create<CurrencyStore>()(
  persist(
    (set, get) => ({
      rates: null,
      lastUpdate: null,
      isLoading: false,
      error: null,
      conversionCache: {},

      fetchRates: async (currencies?: string[]) => {
        try {
          set({ isLoading: true, error: null })
          
          // The exchangeRateService returns the correct type, but we cast it to ensure it matches
          // our ExchangeRates type in the types package
          let exchangeRates: ExchangeRates | null = null
          
          if (Array.isArray(currencies) && currencies.length > 0) {
            // If an array is provided, treat it as a list of currency codes
            logger.info(`Fetching rates for ${currencies.length} currencies`)
            exchangeRates = await exchangeRateService.fetchRates(currencies) as ExchangeRates
          } else {
            // Otherwise, just fetch all rates
            logger.info('Fetching all exchange rates (global data)')
            exchangeRates = await exchangeRateService.fetchLatestRates() as ExchangeRates
          }
          
          if (exchangeRates) {
            logger.info(`Successfully fetched exchange rates with base: ${exchangeRates.base}`)
            logger.debug('Exchange rates data', { 
              base: exchangeRates.base, 
              source: exchangeRates.source,
              currencies: Object.keys(exchangeRates.rates).length
            })
            
            set({ rates: exchangeRates, lastUpdate: Date.now(), isLoading: false })
            return exchangeRates
          } else {
            throw new Error('Failed to fetch exchange rates: no data returned')
          }
        } catch (error) {
          logger.error('Error fetching rates', error)
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Unknown error fetching exchange rates' 
          })
          
          // Return existing rates if available
          const currentRates = get().rates
          if (currentRates) {
            logger.info('Using existing rates as fallback')
            return currentRates
          }
          
          // Otherwise set and return default rates
          logger.warn('No existing rates, returning default USD-only rates')
          const defaultRates: ExchangeRates = { 
            base: 'USD', 
            rates: { USD: 1 }, 
            timestamp: Date.now(), 
            source: 'cache' 
          }
          set({ rates: defaultRates, lastUpdate: Date.now() })
          return defaultRates
        }
      },

      updateRates: async (currencies?: string[]) => {
        // Check if an update is already in progress
        if (isUpdateInProgress) {
          logger.info('Exchange rates update already in progress, skipping duplicate request');
          
          // Return the current rates
          return Promise.resolve();
        }
        
        // Check if we're within the debounce period
        const lastUpdate = get().lastUpdate;
        const now = Date.now();
        if (lastUpdate && now - lastUpdate < UPDATE_DEBOUNCE_MS) {
          logger.info(`Exchange rates updated recently (${Math.floor((now - lastUpdate) / 1000)}s ago), using existing rates`);
          
          // Clear any existing timeout
          if (updateTimeout) {
            clearTimeout(updateTimeout);
            updateTimeout = null;
          }
          
          // Set a new timeout for deferred update
          updateTimeout = setTimeout(() => {
            // Reset update in progress flag
            isUpdateInProgress = false;
            // Retry update
            get().updateRates(currencies);
          }, UPDATE_DEBOUNCE_MS - (now - lastUpdate));
          
          return Promise.resolve();
        }
        
        try {
          isUpdateInProgress = true;
          set({ isLoading: true, error: null })
          let exchangeRates: ExchangeRates | null = null
          
          if (Array.isArray(currencies) && currencies.length > 0) {
            // If an array is provided, treat it as a list of currency codes
            // Filter out invalid currency codes
            const validCurrencies = currencies.filter(code => {
              const isValid = typeof code === 'string' && code.length === 3
              if (!isValid) {
                logger.warn(`Invalid currency code: ${code}`)
              }
              return isValid
            })
            
            if (validCurrencies.length === 0) {
              logger.warn('No valid currencies provided, using defaults')
              // Using extended default currencies list
              const defaultCurrencies = [
                'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 
                'HKD', 'NZD', 'SEK', 'KRW', 'SGD', 'NOK', 'MXN', 'INR', 
                'RUB', 'ZAR', 'TRY', 'BRL', 'TWD', 'AED', 'SAR', 'QAR', 
                'KWD', 'BHD', 'OMR', 'JOD', 'EGP', 'LBP', 'SYP'
              ];
              logger.info(`Using ${defaultCurrencies.length} default currencies`)
              exchangeRates = await exchangeRateService.fetchRates(defaultCurrencies) as ExchangeRates
            } else {
              logger.info(`Updating rates for ${validCurrencies.length} currencies`)
              exchangeRates = await exchangeRateService.fetchRates(validCurrencies) as ExchangeRates
            }
          } else {
            // Otherwise, get all rates
            logger.info('Updating all exchange rates (global data)')
            exchangeRates = await exchangeRateService.fetchLatestRates() as ExchangeRates
          }
          
          if (exchangeRates) {
            logger.info(`Successfully updated exchange rates with base: ${exchangeRates.base}`)
            set({ rates: exchangeRates, lastUpdate: Date.now(), isLoading: false })
          } else {
            throw new Error('Failed to update exchange rates: no data returned')
          }
        } catch (error) {
          logger.error('Error updating rates', error)
          
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Unknown error updating exchange rates' 
          })
          
          // Keep existing rates if available
          if (!get().rates) {
            logger.warn('No existing rates, setting default USD-only rates')
            set({ 
              rates: { 
                base: 'USD', 
                rates: { USD: 1 }, 
                timestamp: Date.now(), 
                source: 'cache' 
              },
              lastUpdate: Date.now()
            })
          }
        } finally {
          // Reset the flag after operation completes
          isUpdateInProgress = false;
        }
      },

      convertAmount: (amount: number, fromCurrency: string, toCurrency: string) => {
        if (fromCurrency === toCurrency) {
          return amount;
        }

        // Get the loaded exchange rates
        const rates = get().rates;
        
        // Validate we have rates
        if (!rates || !rates.rates) {
          logger.warn(`No exchange rates available for conversion from ${fromCurrency} to ${toCurrency}`);
          return amount;
        }
        
        try {
          const ratesData = rates.rates;
          let convertedAmount = amount;
          
          // Both currencies are the same as the base (usually USD)
          if (fromCurrency === rates.base && toCurrency === rates.base) {
            convertedAmount = amount;
          }
          // Direct conversion from base currency (e.g., USD to EUR)
          else if (fromCurrency === rates.base) {
            const toRate = ratesData[toCurrency];
            if (!toRate) {
              logger.warn(`No exchange rate found for ${toCurrency}`);
              return amount;
            }
            convertedAmount = amount * toRate;
          }
          // Direct conversion to base currency (e.g., EUR to USD)
          else if (toCurrency === rates.base) {
            const fromRate = ratesData[fromCurrency];
            if (!fromRate) {
              logger.warn(`No exchange rate found for ${fromCurrency}`);
              return amount;
            }
            convertedAmount = amount / fromRate;
          }
          // Cross-currency conversion (e.g., EUR to TRY)
          else {
            // First convert from source currency to base (USD), then to target currency
            const fromRate = ratesData[fromCurrency];
            const toRate = ratesData[toCurrency];
            
            if (!fromRate || !toRate) {
              const missingCurrency = !fromRate ? fromCurrency : toCurrency;
              logger.warn(`No exchange rate found for ${missingCurrency}`);
              return amount;
            }
            
            // First convert to USD (divide by fromRate), then convert to target (multiply by toRate)
            convertedAmount = (amount / fromRate) * toRate;
          }
          
          // Apply precious metals conversion from ounces to grams
          // API rates are per troy ounce, but we want per-gram rates for precious metals
          // 1 troy ounce = 31.1035 grams
          const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
          
          if (preciousMetals.includes(toCurrency) && !preciousMetals.includes(fromCurrency)) {
            // Converting TO precious metals: result should be per-gram
            // Example: 1 USD = X grams of gold
            // API gives us: 1 USD = Y troy ounces of gold
            // We want: 1 USD = Y * 31.1035 grams of gold
            convertedAmount = convertedAmount * 31.1035;
          } else if (preciousMetals.includes(fromCurrency) && !preciousMetals.includes(toCurrency)) {
            // Converting FROM precious metals: input is per-gram, but API rate is per-ounce
            // Example: 1 gram of gold = X USD
            // API gives us: 1 troy ounce of gold = Y USD
            // We want: 1 gram of gold = Y / 31.1035 USD
            convertedAmount = convertedAmount / 31.1035;
          }
          
          return convertedAmount;
        } catch (error) {
          logger.error('Error converting amount:', error);
          return amount;
        }
      },

      clearRates: () => {
        logger.info('Clearing currency rates')
        set({ rates: null, lastUpdate: null, conversionCache: {} })
      }
    }),
    {
      name: 'currency-store',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        // Only persist these fields to storage
        rates: state.rates,
        lastUpdate: state.lastUpdate
        // conversionCache is intentionally not persisted
      })
    }
  )
) 